import { createClient } from '@supabase/supabase-js'
import { SUPABASE } from '../config'

// Supabase configuration - reads from config.js
const supabaseUrl = SUPABASE.url
const supabaseAnonKey = SUPABASE.anonKey

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Auth helper functions
export const authHelpers = {
  // Expose supabase client for direct access when needed
  supabase,

  // Session and onboarding management
  markOnboardingComplete: () => {
    localStorage.setItem('narcotictrack_onboarding_complete', 'true');
  },

  hasCompletedOnboarding: () => {
    return localStorage.getItem('narcotictrack_onboarding_complete') === 'true';
  },

  setLastActivity: () => {
    localStorage.setItem('narcotictrack_last_activity', Date.now().toString());
  },

  checkSessionTimeout: () => {
    const lastActivity = localStorage.getItem('narcotictrack_last_activity');
    if (!lastActivity) return true; // No activity recorded, consider timed out
    
    const now = Date.now();
    const timeDiff = now - parseInt(lastActivity);
    const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds
    
    return timeDiff > fiveMinutes;
  },

  clearSession: () => {
    localStorage.removeItem('narcotictrack_last_activity');
    // Don't clear onboarding - that stays forever
  },

  // Check if user has valid session and hasn't timed out
  checkValidSession: async () => {
    try {
      // Check if session timed out first
      if (authHelpers.checkSessionTimeout()) {
        await authHelpers.signOut();
        return { data: null, error: 'Session timed out' };
      }

      // Check Supabase session
      const { data, error } = await supabase.auth.getSession();
      
      if (data?.session?.user) {
        // Update activity timestamp
        authHelpers.setLastActivity();
        return { data: data.session, error: null };
      }
      
      return { data: null, error: error || 'No active session' };
    } catch (error) {
      return { data: null, error };
    }
  },
  // Admin-only: create a pre-confirmed account through the admin-create-user
  // edge function. Roles travel separately and land in app_metadata; the PIN
  // in userData is hashed by handle_new_user() and stripped from metadata.
  adminCreateUser: async (email, password, userData = {}, roles = {}) => {
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) {
        return { data: null, error: new Error('Not signed in') }
      }

      const res = await fetch(`${supabaseUrl}/functions/v1/admin-create-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          apikey: supabaseAnonKey,
        },
        body: JSON.stringify({
          email,
          password,
          user_metadata: userData,
          is_admin: roles.isAdmin === true,
          is_editor: roles.isEditor === true,
        }),
      })

      const json = await res.json().catch(() => ({}))
      if (!res.ok) {
        return { data: null, error: new Error(json.error || `Failed to create user (${res.status})`) }
      }

      return { data: { user: json.user }, error: null }
    } catch (error) {
      return { data: null, error }
    }
  },

  signUp: async (email, password, userData = {}) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData // Additional user metadata
        }
      })
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Sign in user
  signIn: async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })
      
      if (data?.session?.user) {
        // Set activity timestamp on successful login
        authHelpers.setLastActivity();
      }
      
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Sign out user
  signOut: async () => {
    try {
      authHelpers.clearSession();
      const { error } = await supabase.auth.signOut()
      return { error }
    } catch (error) {
      return { error }
    }
  },

  // Reset password
  resetPassword: async (email) => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}?type=recovery`
      })
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Update password (used after reset)
  updatePassword: async (newPassword) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      })
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Get current user
  getCurrentUser: () => {
    return supabase.auth.getUser()
  },

  // Listen to auth changes
  onAuthStateChange: (callback) => {
    return supabase.auth.onAuthStateChange(callback)
  }
}

// Database helper functions
export const dbHelpers = {
  // Generic insert function
  insert: async (table, data) => {
    try {
      const { data: result, error } = await supabase
        .from(table)
        .insert(data)
        .select()
      return { data: result, error }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Generic select function
  select: async (table, columns = '*', filters = {}) => {
    try {
      let query = supabase.from(table).select(columns)
      
      // Apply filters if provided
      Object.entries(filters).forEach(([key, value]) => {
        query = query.eq(key, value)
      })
      
      const { data, error } = await query
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Generic update function
  update: async (table, updates, filters) => {
    try {
      let query = supabase.from(table).update(updates)
      
      // Apply filters
      Object.entries(filters).forEach(([key, value]) => {
        query = query.eq(key, value)
      })
      
      const { data, error } = await query.select()
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Generic delete function
  delete: async (table, filters) => {
    try {
      let query = supabase.from(table)
      
      // Apply filters
      Object.entries(filters).forEach(([key, value]) => {
        query = query.delete().eq(key, value)
      })
      
      const { data, error } = await query
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  }
}